<script setup>
import { computed, ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import moment from 'moment'

import { DxList } from 'devextreme-vue'
import { DxItem } from 'devextreme-vue/form'
import { DxTabPanel } from 'devextreme-vue/tab-panel'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import { menuRoutesEnum } from '@/router'

import * as dataGrid from '@/composables/data-grid'
import { usePartialSave } from '@/composables/partial-save'
import { useAddressRef, useLabelController } from '@/composables/data-grid-column'
import { useMultiSelectFilter, useMultiSelectEditorOptions } from '@/composables/data-grid'
import { useColumns } from '@/composables/registry/vehicle-grid.js'
import { useAttachment } from '@/composables/registry/service-attachment-grid'
import { useInfoBikeSearch } from '@/composables/registry/info-bike-search'

import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { usePortalStore } from '@/stores/portal.store'
import { useGarageStore } from '@/stores/garage.store'

import { registryApi } from '@/apis/registry.api'

import AttachmentIconList from '@/components/shared/AttachmentIconList.vue'
import CityDropDownBox from '@/components/shared/CityDropDownBox.vue'
import CountryDropDownBox from '@/components/shared/CountryDropDownBox.vue'
import CustomerDropDownBox from '@/components/shared/CustomerDropDownBox.vue'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'
import InfoBikeSearchCaptionButton from '@/components/shared/InfoBikeSearchCaptionButton.vue'
import InfoBikePlateNumberEditor from '@/components/shared/InfoBikePlateNumberEditor.vue'
import MultiSelectFilter from '@/components/shared/MultiSelectFilter.vue'
import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import ServiceHistoryInterventionGrid from '@/components/shared/ServiceHistoryInterventionGrid.vue'
import StateDropDownBox from '@/components/shared/StateDropDownBox.vue'
import VehicleMovementGrid from '@/components/registry/VehicleMovementGrid.vue'

import { useSubjectService } from '@/services/subject.service'

import { useEntityLock } from '@/services/entity-lock.service'

const gridId = 'vehicle-grid'

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()
const portalStore = usePortalStore()
const garageStore = useGarageStore()

const { gridRef, refreshGridAsync } = dataGrid.useGridRef()
const formInstance = ref(null)

const { route, router } = dataGrid.useGridRouter()

const { applyFilter, setMultiSelectFilterRef, clearMultiSelectFilter } = useMultiSelectFilter()
const { partialSave } = usePartialSave()
const { getAttachmentEntities } = useAttachment()

const { isVatCountryIta, isNameExtMandatory } = useSubjectService()

const gridDataSource = dataGrid.useODataSource(
  registryStore.api.getAbsoluteUrl() + registryStore.api.vehiclesUrl,
  {
    map: (vehicle) => ({
      ...vehicle,
      customer: registryStore.customers.find(c => c.id === vehicle.customer_id) ?? null
    })
  }
)

const { currentCountryId, currentStateId, currentVatCountryId, currentZipCode, currentFilteredCitiesCount, setCellValueCity, setCellValueZipCode, onCountryChanged, onStateChanged } = useAddressRef({ onVatCountryChanged: () => updateFormLabels(editors, subjectLabelFields) })

const currentVehicleFuelType = ref(null)
const currentSupplierBrandId = ref(null)
const currentCustomerTypeId = ref(null)
const currentCustomerSubTypeId = ref(null)
const currentVehicleTypeId = ref(null)
const vehicleSetValue = ref(null)
const isFilteredForEdit = ref(false)
const currentVatCountryCode = computed(() => {
  const country = webSiteStore.countries.find(x => x.id === currentVatCountryId.value)
  return country ? country.iso3_code : null
})
const isDisabled = ref(null)
const editors = {}
const statusFilterRef = ref(null)

const autoEditPending = ref(route.query.openEdit === 'true')

const onGridContentReady = (e) => {
  if (!autoEditPending.value) return

  const dataSource = e.component.getDataSource()
  if (dataSource.isLoading()) return

  const rows = e.component.getVisibleRows()
  if (rows.length === 0) return

  autoEditPending.value = false
  e.component.editRow(rows[0].rowIndex)

  router.replace({ query: { ...route.query, openEdit: undefined } })
}

const {
  infoBikeSearchDisabled, showPulse, pulseCount,
  currentInfoBikeBrandCode, currentInfoBikeModelCode,
  onPlateBlur, infoBikeSearch, loadCreditsAsync,
  onInfoBikeEditorPreparing, infoBikeFormGroup
} = useInfoBikeSearch({
  getEditor: (field) => editors[field],
  getEditingData: () => gridRef.value?.instance?.getVisibleRows().find(row => row.isEditing)?.data,
  setVehicle: (vehicle) => vehicleSetValue.value?.(vehicle),
  beginLoading: () => gridRef.value?.instance?.beginCustomLoading(),
  endLoading: () => gridRef.value?.instance?.endCustomLoading(),
  registryStore,
  garageStore,
  portalStore
})

const entityLock = useEntityLock({
  baseLockUrl: registryStore.api.getAbsoluteUrl() + registryStore.api.vehiclesUrl,
  modelName: 'Vehicle',
  gridRef,
})
const rowButtons = entityLock.getEntityLockButtons()

const COMMERCIAL_NEW_ID = '__commercial_new__'

const implementedVehicleStatusTypes = registryStore.getLocalizedVehicleStatusTypes().filter(i => i.id === registryStore.vehicleStatusTypesEnum.TRANSITED)
const customStatusOption = { id: COMMERCIAL_NEW_ID, name: i18n.t('common.commerciallyNew') }
const statusFilterOptions = [
  {
    key: i18n.t('common.groupStatus'),
    items: [customStatusOption]
  },
  {
    key: i18n.t('common.standard'),
    items: [...registryStore.getLocalizedVehicleStatusTypes()]
  }
]

const readOnlyFields = [
  'customer.name', 'customer.subject_type_ref', 'customer.subject_sub_type_ref',
  'customer.name_ext', 'customer.phone', 'customer.vat_country_id', 'customer.tax_code', 'customer.mobile', 'customer.email',
  'customer.email_certified', 'customer.identity_document_type_ref', 'customer.identity_document_number',
  'customer.identity_document_date', 'customer.birth_date', 'customer.address.street',
  'customer.address.zip_code'
]
const disabledOwnerFields = ['subject_type_ref', 'subject_sub_type_ref', 'name']
const addressRequiredFields = ['country', 'zip_code', 'street']

const makeFieldsReadOnly = (e, fields, readOnlyValue) => {
  if (e.parentType === 'dataRow' && fields.includes(e.dataField)) {
    e.editorOptions.disabled = !!readOnlyValue
  }
}

const onEditingClosed = async() => {
  if (isFilteredForEdit.value) {
    const grid = gridRef.value.instance
    grid.filter(null)
    isFilteredForEdit.value = false
  }
}

const openEditModalById = async(id) => {
  const gridInstance = gridRef.value.instance
  isFilteredForEdit.value = true

  gridInstance.filter(['code', '=', id])
  await gridInstance.getDataSource().reload()

  const items = gridInstance.getDataSource().items()

  if (items.length === 0) {
    console.warn(`Vehicle ${id} not found`)
    isFilteredForEdit.value = false
    return
  }

  const rowIndex = gridInstance.getRowIndexByKey(items[0].id)
  if (rowIndex === -1) return

  gridInstance.editRow(rowIndex)
}

const getFilterDataSource = (field) => {
  const buildFilterOptions = (items) => [{
    items: [...items]
  }]

  switch (field) {
    case 'vehicle_type':
      return buildFilterOptions(portalStore.getLocalizedVehicleTypes())
    default:
      return []
  }
}

const isSaveDisabled = (key, requiredFields, addressFields = []) => {
  const grid = gridRef.value?.instance
  if (!grid) return true

  const editingRow = grid.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return true

  const obj = editingRow.data?.[key]
  if (!obj) return true

  if (obj.id) return true

  const isMissingRequired = requiredFields.some(field => !obj[field])
  if (isMissingRequired) return true

  if (key === 'customer' && isNameExtMandatory(obj.subject_type_ref) && !obj.name_ext) return true

  if (addressFields && addressFields.length > 0 && obj['address']) {
    const hasSomeExtra = addressFields.some(field => !!obj['address'][field])

    if (hasSomeExtra) {
      const hasAllExtra = addressRequiredFields.every(field => !!obj['address'][field])

      const hasState = !!(obj['address']['state'] || obj['address']['state_text'])
      const hasCity = !!(obj['address']['city'] || obj['address']['city_text'])

      if (!(hasAllExtra && hasState && hasCity)) return true
    }
  }

  return false
}

const columns = useColumns(
  editors,
  currentVehicleFuelType,
  currentSupplierBrandId,
  currentVehicleTypeId,
  currentCountryId,
  currentStateId,
  currentVatCountryId,
  currentVatCountryCode,
  currentCustomerTypeId,
  currentCustomerSubTypeId,
  currentInfoBikeBrandCode,
  currentInfoBikeModelCode,
  setCellValueZipCode,
  setCellValueCity
)

const { customizeItem, updateFormLabels, registerLabelEditors } = useLabelController({ vehicleFuelType: currentVehicleFuelType, subjectType: currentCustomerTypeId, subjectSubTypeId: currentCustomerSubTypeId, vatCountryId: currentVatCountryId })
const subjectLabelFields = ['customer.name', 'customer.name_ext', 'customer.subject_sub_type_ref', 'customer.vat_number', 'customer.tax_code']

const { defaultDialogTitle } = dataGrid.useDefaultDialogTitle()

const editing = dataGrid.useDefaultEditing(i => defaultDialogTitle(i, i18n.t('common.vehicle')), {
  colCount: 2,
  items: [
    infoBikeFormGroup(i18n, garageStore, 2),
    {
      itemType: 'group',
      items: [
        {
          itemType: 'group',
          caption: i18n.t('common.mainInfo'),
          name: 'mdi-motorbike',
          captionTemplate: 'generic-caption-template',
          colCount: 3,
          items: [
            { dataField: 'id', editorOptions: { readOnly: true } },
            {
              dataField: 'status',
              editorType: 'dxSelectBox',
              editorOptions: {
                dataSource: implementedVehicleStatusTypes,
                valueExpr: 'id',
                displayExpr: 'name',
                showClearButton: true,
              }
            },
            { dataField: 'infoBikeCodeFull', editorOptions: { readOnly: true } },
            { dataField: 'supplier_brand_ref', editorOptions: { showClearButton: true } },
            { dataField: 'name', colSpan: 2, validationRules: [{ type: 'required' }] },
            { dataField: 'engine_size', editorType: 'dxNumberBox' },
            { dataField: 'engine_power_kw', editorType: 'dxNumberBox', editorOptions: { min: 0 } },
            { dataField: 'color' },
            { dataField: 'plate_number', editorOptions: { maxLength: 7 } },
            { dataField: 'vin', editorOptions: { maxLength: 17 } },
            { dataField: 'engine_code' },
            { dataField: 'km', editorOptions: { min: 0 } },
            { dataField: 'vehicle_type', editorType: 'dxSelectBox', editorOptions: { showClearButton: true } },
            { dataField: 'fuel_type', editorType: 'dxSelectBox', editorOptions: { showClearButton: true } },
            { dataField: 'emission_standard', editorType: 'dxSelectBox', editorOptions: { showClearButton: true } },
            { dataField: 'co2_emissions', editorType: 'dxNumberBox', editorOptions: { min: 0 } },
            { dataField: 'transmission_type', editorType: 'dxSelectBox', editorOptions: { showClearButton: true } },
            { dataField: 'notes', colSpan: 3 },
          ]
        },
        {
          itemType: 'group',
          caption: i18n.t('common.vehicleInfo', 2),
          name: 'mdi-calendar-month',
          captionTemplate: 'generic-caption-template',
          colCount: 3,
          items: [
            { dataField: 'enrollment_date', editorType: 'dxDateBox' },
            { dataField: 'road_tax_due_date', editorType: 'dxDateBox' },
            { dataField: 'revision_due_date', editorType: 'dxDateBox' },
          ]
        }
      ]
    },
    {
      itemType: 'group',
      items: [
        {
          itemType: 'group',
          caption: i18n.t('common.additionalInfo'),
          name: 'mdi-account-circle',
          captionTemplate: 'owner-info-caption-template',
          colCount: 6,
          items: [
            { dataField: 'customer_id', colSpan: 2 },
            { dataField: 'customer.subject_type_ref', colSpan: 2 },
            { dataField: 'customer.subject_sub_type_ref', colSpan: 2 },
            { dataField: 'customer.name', label: { text: i18n.t('common.lastName') }, colSpan: 3 },
            { dataField: 'customer.name_ext', label: { text: i18n.t('common.name') }, colSpan: 3 },
            ...dataGrid.useDefaultAddressEditing('customer.address', 3, null, {}, true),
            { dataField: 'customer.vat_country_id', label: { text: i18n.t('common.vatCountry')}, colSpan: 2 },
            { dataField: 'customer.vat_number', label: { text: i18n.t('common.vatNumber') }, colSpan: 2 },
            { dataField: 'customer.tax_code', label: { text: i18n.t('common.taxCode') }, colSpan: 2 },
            { dataField: 'customer.phone', editorOptions: { onContentReady: (e) => { dataGrid.usePhoneMask(e.element) }}, colSpan: 3 },
            { dataField: 'customer.mobile', editorOptions: { onContentReady: (e) => { dataGrid.usePhoneMask(e.element, { definitions: dataGrid.useMask().mobileMaskRules }) }}, colSpan: 3 },
            { dataField: 'customer.email', colSpan: 3 },
            { dataField: 'customer.email_certified', colSpan: 3 },
            { dataField: 'customer.identity_document_type_ref', colSpan: 3 },
            { dataField: 'customer.identity_document_number', colSpan: 3 },
            { dataField: 'customer.identity_document_date', editorOptions: { max: new Date() }, colSpan: 3 },
            { dataField: 'customer.birth_date', colSpan: 3 },
          ]
        }
      ]
    }
  ],
  onInitialized: (e) => { formInstance.value = e.component },
  customizeItem: customizeItem,
}, {
  onInitializedCustom: (e) => entityLock.readOnlyToolbarItems(e),
  entityLock: entityLock,
})

const onEditingStart = async(e) => {
  currentVehicleFuelType.value = e.data?.fuel_type || null
  currentSupplierBrandId.value = e.data?.supplier_brand_ref || null
  currentVehicleTypeId.value = e.data?.vehicle_type || null
  currentVatCountryId.value = e.data?.vat_country_id || null
  currentCountryId.value = e.data?.customer?.address?.country || null
  currentStateId.value = e.data?.customer?.address?.state || null
  if (!e.data.infoBike) {
    e.data.infoBike = {}
  }
  e.data.infoBike.plate_number = e.data.plate_number ?? ''
  infoBikeSearchDisabled.value = !!e.data.infoBikeCodeFull
  await loadCreditsAsync()
}

const onTabPanelSelectionChanged = () => { gridRef.value.instance.updateDimensions() }

const shouldEnableVat = (customer) => {
  return isVatCountryIta(customer?.vat_country_id)
}

const trackedEditorFields = [
  'supplier_brand_ref',
  'infoBikeCodeFull',
  'name',
  'engine_size',
  'engine_power_kw',
  'vehicle_type',
  'engine_code',
  'co2_emissions',
  'emission_standard',
  'transmission_type',
]

const onEditorPreparing = (e) => {
  const defaultValueChanged = e.editorOptions.onValueChanged

  if (e.parentType === 'dataRow' && (e.dataField === 'customer.birth_date' || e.dataField === 'customer.identity_document_date')) {
    e.editorOptions.onValueChanged = (args) => {
      args.value = args.value && moment(args.value).format(dataGrid.useMask().dateSimpleFormat)
      defaultValueChanged(args)
    }
  }

  onInfoBikeEditorPreparing(e, editors, '')

  if (e.parentType === 'dataRow' && !!e.dataField && trackedEditorFields.includes(e.dataField)) {
    const dataField = e.dataField
    e.editorOptions.onInitialized = (e) => { editors[dataField] = e.component }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'customer.vat_number') {
    e.editorOptions.disabled = !shouldEnableVat(e.row?.data?.customer) || e.row?.data?.customer_id
    e.editorOptions.onInitialized = (e) => { editors['customer.vat_number'] = e.component }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'customer.tax_code') {
    e.editorOptions.onInitialized = (e) => { editors['customer.tax_code'] = e.component }
  }

  if (e.parentType === 'filterRow' && e.dataField === 'status') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      contentTemplate: 'multiStatusSelectFilterRef',
      dataSource: registryStore.getLocalizedVehicleStatusTypes(),
      field: e.dataField,
    })
  }

  if (e.parentType === 'filterRow' && e.dataField === 'vehicle_type') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: portalStore.getLocalizedVehicleTypes(),
      field: e.dataField,
    })
  }

  if (e.parentType === 'dataRow' && e.dataField === 'fuel_type') {
    currentVehicleFuelType.value = e.row?.data?.fuel_type
    e.editorOptions.onInitialized = (e) => { editors['vehicle_fuel_type'] = e.component }
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      currentVehicleFuelType.value = args.value
      updateFormLabels(editors, ['engine_size'])
    }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'vin') {
    e.editorOptions.onInitialized = (e) => { editors['vin'] = e.component }
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      let newValue = args.value || ''
      newValue = newValue.toUpperCase()
      args.component.option('value', newValue)
    }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'plate_number') {
    e.editorOptions.onInitialized = (e) => { editors['plate_number'] = e.component }
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      let newValue = args.value || ''
      newValue = newValue.toUpperCase()
      if (!registryStore.companyData.vehicle_plate_allow_blank_spaces) {
        newValue = newValue.replace(/\s+/g, '')
      }
      args.component.option('value', newValue)
    }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'id' && e.row?.data?.customer_id) {
    e.row.data.customer = registryStore.customers.find(c => c.id === e.row.data.customer_id)
    currentCountryId.value = e.row?.data?.customer?.address?.country || null
    currentStateId.value = e.row?.data?.customer?.address?.state || null
    currentVatCountryId.value = e.row?.data?.customer?.vat_country_id || null
  }

  if (e.parentType === 'dataRow' && (e.dataField === 'customer.subject_type_ref' || e.dataField === 'customer.subject_sub_type_ref' || e.dataField === 'customer.name' || e.dataField === 'customer.name_ext' ||
                                      e.dataField === 'customer.address.zip_code' || e.dataField === 'customer.address.street' || e.dataField === 'customer.address.state' ||
                                      e.dataField === 'customer.address.country' || e.dataField === 'customer.address.city' ||
                                      e.dataField === 'customer.mobile' || e.dataField === 'customer.tax_code')) {
    isDisabled.value = isSaveDisabled('customer', disabledOwnerFields, addressRequiredFields)
    const defaultValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      if (e.dataField === 'customer.subject_type_ref') {
        currentCustomerTypeId.value = args.value
        updateFormLabels(editors, subjectLabelFields)
      }
      if (e.dataField === 'customer.subject_sub_type_ref') {
        currentCustomerSubTypeId.value = args.value
        updateFormLabels(editors, subjectLabelFields)
      }
      isDisabled.value = isSaveDisabled('customer', disabledOwnerFields, addressRequiredFields)
    }
  }
  registerLabelEditors(e, editors, ['engine_size', 'customer.name', 'customer.name_ext', 'customer.subject_sub_type_ref'])
  if (e.parentType === 'dataRow') {
    makeFieldsReadOnly(e, readOnlyFields, e.row.data?.customer_id)
  }
}

const onInitNewRow = async(e) => {
  const params = {
    field: 'id',
  }
  e.promise = registryStore.api.getVehicleNextIdAsync(params).then(res => { e.data.id = res.data })
  currentVehicleFuelType.value = null
  currentSupplierBrandId.value = null
  currentVehicleTypeId.value = null
  currentVatCountryId.value = null
  currentCustomerTypeId.value = null
  currentInfoBikeModelCode.value = null

  e.data.km = 0

  if (registryStore.companyData.default_vat_country) {
    e.data.vat_country_id = currentVatCountryId.value = registryStore.companyData.default_vat_country
  }

  await loadCreditsAsync()
}

const { repaintRow } = dataGrid.useUpdateMasterDetail()

const onDetailRowSaved = (parentId, parentRowKey) => {
  repaintRow(gridRef, registryStore.api.getVehiclesAsync, parentId, parentRowKey)
}

const saveNewOwner = async() => {
  const grid = gridRef.value?.instance
  if (!grid) return

  const editingRow = grid.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return

  const rowIndex = editingRow?.rowIndex
  const owner = editingRow?.data?.customer

  if (!owner || owner.id || editingRow?.data?.customer_id) {
    grid.getController('validating')?.validate(true)
    return
  }

  const nextIdResponse = await registryStore.api.getSubjectNextIdAsync()
  owner.code = nextIdResponse.data
  owner.type = registryStore.subjectEntityTypes.CUSTOMER
  owner.customer ??= {}
  owner.customer.discount_list_ref = registryStore.getLocalizedDiscountLists()[0]?.id
  delete owner.id
  delete owner.address

  const customerFields = [
      'identity_document_type_ref',
      'identity_document_date',
      'identity_document_number',
      'birth_date'
  ]
  for (const field of customerFields) {
    if (owner[field] !== undefined) {
      owner.customer[field] = owner[field]
      delete owner[field]
    }
  }

  const res = await partialSave(owner, registryApi.createSubjectAsync, { grid: grid })
  if (res?.customer) {
    await registryStore.fetchCustomersAsync(true)

    const rowData = editingRow?.data ?? {}

    rowData.customer_id = res.customer.id
    rowData.customer = {...res.customer}
    grid.cellValue(rowIndex, 'customer_id', res.customer.id)
    formInstance.value?.repaint()
  }
}

const onClearFilter = () => {
  clearMultiSelectFilter()
  statusFilterRef.value?.instance?.option('selectedItemKeys', [])
}

const onRowInserted = async() => {
  await registryStore.fetchVehiclesAsync(true)
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: editing,
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  onEditingStart: onEditingStart,
  onEditorPreparing: onEditorPreparing,
  onInitNewRow: onInitNewRow,
  onClearFilter: onClearFilter,
  onRowInserted: onRowInserted,
  onSaved: onEditingClosed,
  onEditCanceled: onEditingClosed,
  onContentReady: onGridContentReady,
  onToolbarPreparing: (e) => {
    dataGrid.useDefaultToolbar(e, {
      addButtonOptions: { hint: `${i18n.t('common.add')} ${i18n.t('common.vehicle')}` },
    })
    e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
  },
  height: '90vh',
  routerFilter: {
    fields: [{code: 'id', vin: 'vin'}],
    getRoute: () => route,
    router: router
  }
})
</script>
<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #attachmentsCellTemplate="{ data: cellInfo }">
        <attachment-icon-list
          :data="cellInfo.data"
          document-name="common.vehicle"
          :has-attachments="cellInfo.data?.has_attachments"
          :document-field="'attachment_vehicle'"
          :form-data-source="{
            attachment_category: portalStore.attachmentCategoryEnum.VEHICLE,
          }"
          :entities-id="getAttachmentEntities(cellInfo.data, ['vehicle', 'subject'])"
          :refresh-grid-async="() => refreshGridAsync()"
        />
      </template>
      <template #gridLayoutConfigTemplate>
        <grid-layout-config-template
          :grid-ext-ref="gridRef"
          :grid-ext-id="gridId"
        />
      </template>
      <template #generic-caption-template="{ data: cellInfo }">
        <div
          class="dx-form-group-caption-wrapper"
          style="min-height: 1.5rem;"
        >
          <q-icon
            :name="cellInfo.name"
            class="dx-form-group-caption-icon"
          />
          <span class="dx-form-group-caption-text">
            {{ cellInfo.caption }}
          </span>
        </div>
      </template>
      <template #isPartialCellTemplate="{ data: templateOptions }">
        <q-chip
          size="sm"
          text-color="white"
          style="margin: 0"
          :label="templateOptions.data.is_partial ? i18n.t('common.partial') : i18n.t('common.complete')"
          :color="templateOptions.data.is_partial ? 'orange' : 'grey'"
        />
      </template>
      <template #owner-info-caption-template="{ data: cellInfo }">
        <div
          class="dx-form-group-caption"
          style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
        >
          <div class="dx-form-group-caption-wrapper">
            <q-icon
              :name="cellInfo.name"
              class="dx-form-group-caption-icon"
            />
            <span class="dx-form-group-caption-text">
              {{ cellInfo.caption }}
            </span>
          </div>
          <div>
            <q-btn
              icon="mdi-content-save"
              flat
              size="xs"
              round
              :disabled="isDisabled"
              @click="saveNewOwner"
            />
          </div>
        </div>
      </template>
      <template #customerEditor="{ data: cellInfo }">
        <customer-drop-down-box
          show-clear-button
          :data="cellInfo"
          :disabled="!cellInfo.row?.isNewRow || entityLock.isLocked()"
        />
      </template>
      <template #cityEditor="{ data: cellInfo }">
        <city-drop-down-box
          :data="cellInfo"
          :value="cellInfo.data.customer?.address?.city"
          :value-text="cellInfo.data.customer?.address?.city_text"
          :country="currentCountryId"
          :state="currentStateId"
          :zip-code="currentZipCode"
          :filtered-cities-count="currentFilteredCitiesCount"
          :disabled="!!cellInfo.data.customer_id || entityLock.isLocked()"
        />
      </template>
      <template #stateEditor="{ data: cellInfo }">
        <div>
          <state-drop-down-box
            :data="cellInfo"
            :value="cellInfo.data.customer?.address?.state"
            :value-text="cellInfo.data.customer?.address?.state_text"
            :country="currentCountryId"
            :disabled="!!cellInfo.data.customer_id || entityLock.isLocked()"
            @value-changed="onStateChanged($event)"
          />
        </div>
      </template>
      <template #serviceHistoryRef="{ data: cellInfo }">
        <reference-link
          v-if="!!cellInfo.data.vin"
          :data="{
            prefix: cellInfo.data.vin,
            vin: cellInfo.data.vin,
          }"
          :text-fields="['prefix']"
          :query-fields="['vin']"
          :target="menuRoutesEnum.SERVICE_HISTORY"
        />
      </template>
      <template #countryEditor="{ data: cellInfo }">
        <country-drop-down-box
          :data="cellInfo"
          :is-portal="['customer.vat_country_id'].includes(cellInfo.column?.dataField)"
          :disabled="!!cellInfo.data.customer_id || entityLock.isLocked()"
          @value-changed="onCountryChanged($event, cellInfo.column?.dataField)"
        />
      </template>
      <template #vatCountryEditor="{ data: cellInfo }">
        <country-drop-down-box
          :data="cellInfo"
          :is-portal="['customer.vat_country_id'].includes(cellInfo.column?.dataField)"
          :disabled="!!cellInfo.data.customer?.id"
          @value-changed="onCountryChanged($event, cellInfo.column?.dataField, type='vat_country')"
        />
      </template>
      <template #master-detail="{ data: templateOptions }">
        <dx-tab-panel
          animation-enabled
          @selection-changed="onTabPanelSelectionChanged"
        >
          <dx-item
            :title="$t('common.movement', 2)"
            template="movements-tab"
          />
          <template #movements-tab>
            <vehicle-movement-grid
              :vehicle="templateOptions"
              :entity-lock="entityLock"
              @row-saved="onDetailRowSaved"
            />
          </template>

          <dx-item
            :title="$t('common.intervention', 2)"
            template="interventions-tab"
          />
          <template #interventions-tab>
            <service-history-intervention-grid
              :vehicle="templateOptions"
            />
          </template>
        </dx-tab-panel>
      </template>
      <template #multiSelectFilterRef="{ data }">
        <multi-select-filter
          :ref="el => setMultiSelectFilterRef(data.component.option('field'), el)"
          :data-source="getFilterDataSource(data.component.option('field'))"
          :field="data.component.option('field')"
          :grid-ref="gridRef"
          :apply-filter="applyFilter"
          v-bind="{ data }"
        />
      </template>
      <template #multiStatusSelectFilterRef="{ data }">
        <dx-list
          ref="statusFilterRef"
          :data-source="statusFilterOptions"
          display-expr="name"
          value-expr="id"
          :show-selection-controls="true"
          selection-mode="multiple"
          :select-by-click="true"
          :show-select-all-checkbox="false"
          :grouped="true"
          @selection-changed="e => {
            const selected = e.component.option('selectedItemKeys').map(item => item.id)
            const field = data.component.option('field')

            const resolved = new Set()

            selected.forEach(id => {
              if (id === COMMERCIAL_NEW_ID) {
                resolved.add(registryStore.vehicleStatusTypesEnum.NEW)
                resolved.add(registryStore.vehicleStatusTypesEnum.KM0)
                resolved.add(registryStore.vehicleStatusTypesEnum.DEMO)
              } else {
                resolved.add(id)
              }
            })

            const finalSelected = Array.from(resolved)
            data.component.option('value', finalSelected)
            applyFilter(finalSelected, field, gridRef, mainFilter)
          }"
        >
          <template #groupTemplate="{ data: groupData }">
            <div class="group-header">
              {{ groupData.key }}
            </div>
          </template>
        </dx-list>
      </template>
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'Vehicle'"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="entityLock"
          :is-locked-available="true"
          :buttons="rowButtons"
        />
      </template>
      <template #customToolbarAddButtonTemplate>
        <custom-toolbar-add-button-template
          :grid="gridRef"
          :entity-lock="entityLock"
        />
      </template>
      <template #vehicle-infoBike-search-caption-template="{ data }">
        <div
          class="dx-form-group-caption"
          style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
        >
          <div class="dx-form-group-caption-wrapper">
            <q-icon
              :name="data.name"
              class="dx-form-group-caption-icon"
            />
            <span class="dx-form-group-caption-text">
              {{ i18n.t('common.infoBikeSearch', {
                credits: garageStore.infoBikePlateSearchCredit?.credits ?? 0
              }) }}
            </span>
          </div>
          <div>
            <info-bike-search-caption-button
              :disabled="infoBikeSearchDisabled"
              :show-pulse="showPulse"
              :pulse-count="pulseCount"
              :tooltip="i18n.t('common.infoBike')"
              @search="infoBikeSearch('')"
              @pulse-end="showPulse = false"
            />
          </div>
        </div>
      </template>
      <template #infoBikePlateNumberEditor="{ data }">
        <info-bike-plate-number-editor
          :cell-info="data"
          :readonly="!!data.data?.code"
          @update="v => data.setValue(v)"
          @blur="() => onPlateBlur(openEditModalById)"
        />
      </template>
    </dx-data-grid>
  </div>
</template>

<style>
.group-header {
  font-weight: 600;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  color: #495057;
}

.dx-form-group-caption-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-text {
  line-height: 1.2;
}
</style>
